import { useMemo, useState } from "react"
import { Button, Card, Empty, Space } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useCrud } from "@/hooks/useCrud"
import { ID } from "@/types/common"
import systemDictApi from "./api"
import DictModal from "./components/DictModal"
import DictTable from "./components/DictTable"
import { SystemDict, SystemDictFormData } from "./types"

const SystemDictManagement = () => {
  const [parentForCreate, setParentForCreate] = useState<SystemDict | null>(null)
  const [createMode, setCreateMode] = useState<"CATEGORY" | "ITEM">("CATEGORY")
  const [selectedCategoryId, setSelectedCategoryId] = useState<ID | null>(null)

  const api = useMemo(
    () => ({
      getList: () => systemDictApi.getList(),
      create: systemDictApi.create,
      update: (id: ID, data: SystemDictFormData | Partial<SystemDictFormData>) =>
        systemDictApi.update(id, data as SystemDictFormData),
      delete: systemDictApi.delete,
    }),
    []
  )

  const {
    items,
    loading,
    opLoading,
    isModalOpen,
    editingItem,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleCloseModal,
  } = useCrud<SystemDict, SystemDictFormData>({
    api,
    entityName: "字典",
  })

  const categoryItems = useMemo(
    () => items.filter((item) => item.nodeType === "CATEGORY"),
    [items],
  )
  const categoryRows = useMemo(
    () =>
      categoryItems.map(({ children, ...rest }) => ({
        ...rest,
      })),
    [categoryItems],
  )
  const selectedCategory = useMemo(
    () => categoryItems.find((item) => item.id === selectedCategoryId) ?? null,
    [categoryItems, selectedCategoryId],
  )
  const dictItems = useMemo(
    () => (selectedCategory?.children ?? []).filter((item) => item.nodeType === "ITEM"),
    [selectedCategory],
  )

  const openCreateCategory = () => {
    setCreateMode("CATEGORY")
    setParentForCreate(null)
    handleAdd()
  }

  const openCreateItem = () => {
    if (!selectedCategory) {
      return
    }
    setCreateMode("ITEM")
    setParentForCreate(selectedCategory)
    handleAdd()
  }

  const openCreateItemChild = (item: SystemDict) => {
    setCreateMode("ITEM")
    setParentForCreate(item)
    handleAdd()
  }

  const openEdit = (item: SystemDict) => {
    setParentForCreate(null)
    handleEdit(item)
  }

  const closeModal = () => {
    setParentForCreate(null)
    setCreateMode("CATEGORY")
    handleCloseModal()
  }

  const saveItem = async (data: SystemDictFormData) => {
    await handleSave(data)
    setParentForCreate(null)
    setCreateMode("CATEGORY")
  }

  const handleDeleteCategory = async (id: ID) => {
    await handleDelete(id)
    if (selectedCategoryId === id) {
      setSelectedCategoryId(null)
    }
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(500px,600px)_1fr]">
        <Card
          title="字典类型"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateCategory}
              loading={opLoading}
            >
              新增字典类型
            </Button>
          }
        >
          <div className="mb-3 text-sm text-gray-500">
            共 {categoryItems.length} 个字典类型
          </div>
          <DictTable
            items={categoryRows}
            loading={loading}
            selectedIds={selectedIds}
            onSelectionChange={(ids) => {
              setSelectedIds(ids)
              setSelectedCategoryId(ids[0] ?? null)
            }}
            onEdit={openEdit}
            onDelete={handleDeleteCategory}
          />
        </Card>

        <Card
          title={selectedCategory ? `字典项 - ${selectedCategory.name}` : "字典项"}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateItem}
              disabled={!selectedCategory}
              loading={opLoading}
            >
              新增字典项
            </Button>
          }
        >
          {!selectedCategory ? (
            <Empty description="请先在左侧选择一个字典类型" />
          ) : (
            <>
              <div className="mb-3 text-sm text-gray-500">
                当前类型共 {dictItems.length} 个字典项
              </div>
              <DictTable
                items={dictItems}
                loading={loading}
                expandable
                onAddChild={openCreateItemChild}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            </>
          )}
        </Card>
      </div>

      <DictModal
        isOpen={isModalOpen}
        loading={opLoading}
        item={editingItem}
        parentForCreate={parentForCreate}
        createMode={createMode}
        allItems={items}
        onClose={closeModal}
        onSave={saveItem}
      />
    </div>
  )
}

export default SystemDictManagement
